import type { CellContext, RowData } from '@tanstack/react-table';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useStagedValueContext } from './staged-value-context';

type UseEditableCellValueArgs<TData extends RowData, TValue> = {
  context: CellContext<TData, TValue>;
  rowId: string;
  columnId: string;
};

export function useEditableCellValue<TData extends RowData, TValue = unknown>({
  context,
  rowId,
  columnId,
}: UseEditableCellValueArgs<TData, TValue>) {
  const { subscribeStagedValue, getStagedValue, setStagedValue, clearStagedValue } =
    useStagedValueContext();
  const rowValue = context.getValue();

  const subscribeStaged = useCallback(
    (listener: () => void) => subscribeStagedValue(rowId, columnId, listener),
    [subscribeStagedValue, rowId, columnId],
  );
  const getStagedSnapshot = useCallback(
    () => getStagedValue(rowId, columnId),
    [getStagedValue, rowId, columnId],
  );
  const stagedValue = useSyncExternalStore(subscribeStaged, getStagedSnapshot);
  const hasStagedValue = stagedValue !== undefined;
  const value = hasStagedValue ? stagedValue : rowValue;

  // Custom cell renderers must read this column through getValue() to receive staged overrides.
  // row.original remains the canonical row data and does not include staged values.
  const displayContext = useMemo(
    () =>
      hasStagedValue
        ? ({ ...context, getValue: () => stagedValue } as CellContext<TData, TValue>)
        : context,
    [context, hasStagedValue, stagedValue],
  );

  const stageValue = useCallback(
    (nextStagedValue: unknown) => {
      setStagedValue(rowId, columnId, nextStagedValue);
    },
    [columnId, rowId, setStagedValue],
  );

  const clearValue = useCallback(() => {
    clearStagedValue(rowId, columnId);
  }, [clearStagedValue, columnId, rowId]);

  return {
    value,
    stagedValue,
    hasStagedValue,
    displayContext,
    stageValue,
    clearValue,
  };
}
